import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '../ui'
import { useMappingSummary } from '../../api/hooks'
import type { AccessSystem, MappingSummary } from '../../api/types'
import { kindLabels } from './MappingCard'

/** Total undecided candidates across every kind of a mapping summary. */
export function undecidedTotal(summary: Record<string, MappingSummary> | undefined): number {
  return Object.values(summary ?? {}).reduce((sum, counts) => sum + counts.undecided, 0)
}

/** Undecided split into real mapping candidates vs unknown-credential
 *  sightings (the `new_credential` kind) — a sighting is a review chore,
 *  not a mapping decision, and lumping them made the dashboard read 45
 *  where the workbench read 1. */
export function undecidedSplit(summary: Record<string, MappingSummary> | undefined): {
  mapping: number
  sightings: number
} {
  const sightings = summary?.new_credential?.undecided ?? 0
  return { mapping: undecidedTotal(summary) - sightings, sightings }
}

/**
 * The "you have things to do" banner: when a system has undecided mapping
 * candidates, a gold callout pinned ABOVE the system detail's cards routes
 * the operator to the workbench. Renders nothing while the summary loads,
 * on error (a 400 is the provider verdict "no mapping kinds"), and when
 * everything is decided or skipped — the MappingCard below stays the
 * detailed per-kind view.
 */
export function MappingCallout({ system, onOpen }: { system: AccessSystem; onOpen: () => void }) {
  const summary = useMappingSummary(system.unique_id)
  const { mapping, sightings } = undecidedSplit(summary.data)
  const total = mapping + sightings
  if (!summary.data || total === 0) return null

  const kinds = Object.entries(summary.data)
    .filter(([kind, counts]) => counts.undecided > 0 && kind !== 'new_credential')
    .map(([kind]) => (kindLabels[kind] ?? kind).toLowerCase())

  return (
    <div className="flex flex-wrap items-center gap-5 rounded-md border-2 border-ostrel-gold/70 bg-ostrel-gold/15 px-6 py-5 shadow-sm">
      <span className="inline-block h-3 w-3 shrink-0 rotate-45 bg-ostrel-gold" />
      <p className="text-base text-ostrel-ink">
        <span className="tnum text-xl font-bold">
          {mapping > 0 ? `${mapping} ${mapping === 1 ? 'object' : 'objects'} to map` : 'Nothing to map'}
        </span>
        {kinds.length > 0 && mapping > 0 && (
          <span className="text-ostrel-ink-muted"> — {kinds.join(', ')} waiting for review</span>
        )}
        {sightings > 0 && (
          <span className="text-ostrel-ink-muted">
            {mapping > 0 ? ' · ' : ' — '}
            {sightings} unknown {sightings === 1 ? 'tag' : 'tags'} to review
          </span>
        )}
      </p>
      <Button variant="navy" size="lg" className="ml-auto text-base" onClick={onOpen}>
        Open workbench →
      </Button>
    </div>
  )
}

/**
 * Per-row pending-mapping badge on the systems list: a gold dot + count for
 * systems with undecided candidates. One summary fetch per listed system
 * (they are few, and the query key is shared with the detail page's reads);
 * renders nothing while loading, on error, or when nothing is pending.
 */
export function MappingBadge({ systemUid }: { systemUid: string }) {
  const summary = useMappingSummary(systemUid)
  const total = undecidedTotal(summary.data)
  if (total === 0) return null
  return (
    <span
      className="mt-0.5 flex items-center gap-1.5"
      title={`${total} mapping ${total === 1 ? 'candidate' : 'candidates'} waiting for review`}
    >
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-ostrel-gold" />
      <span className="tnum text-[11px] font-semibold text-ostrel-gold">{total} to map</span>
    </span>
  )
}

/** Invisible probe: fetches one system's mapping summary and reports its
 *  undecided split up to the dashboard callout (hooks can't sit in a loop). */
function SystemTotalProbe({
  uid,
  onReport,
}: {
  uid: string
  onReport: (uid: string, split: { mapping: number; sightings: number }) => void
}) {
  const summary = useMappingSummary(uid)
  const split = undecidedSplit(summary.data)
  useEffect(() => onReport(uid, split), [uid, split.mapping, split.sightings, onReport])
  return null
}

/**
 * Dashboard-level mapping to-do CTA: when ANY system has undecided mapping
 * candidates, a gold hero banner at the top of the Overview page sums the
 * backlog and links each pending system to its workbench. One summary fetch
 * per system (they are few; the key is shared with MappingBadge and the
 * detail page). A 400 summary is the provider verdict "no mapping kinds" →
 * that system reports zero. Renders nothing while summaries load and when
 * nothing is pending anywhere. Sightings (unknown tags) are broken out of
 * the headline — they're a review chore, not a mapping decision, and
 * lumping them made this read 45 where the workbench read 1.
 */
export function MappingDashboardCallout({ systems }: { systems: AccessSystem[] }) {
  const [splits, setSplits] = useState<Record<string, { mapping: number; sightings: number }>>({})
  const report = useCallback((uid: string, split: { mapping: number; sightings: number }) => {
    setSplits((prev) =>
      prev[uid]?.mapping === split.mapping && prev[uid]?.sightings === split.sightings
        ? prev
        : { ...prev, [uid]: split },
    )
  }, [])

  const pending = systems
    .map((system) => ({ system, split: splits[system.unique_id] ?? { mapping: 0, sightings: 0 } }))
    .filter((entry) => entry.split.mapping + entry.split.sightings > 0)
  const totalMapping = pending.reduce((sum, entry) => sum + entry.split.mapping, 0)
  const totalSightings = pending.reduce((sum, entry) => sum + entry.split.sightings, 0)

  return (
    <>
      {systems.map((system) => (
        <SystemTotalProbe key={system.unique_id} uid={system.unique_id} onReport={report} />
      ))}
      {pending.length > 0 && (
        <div className="flex flex-wrap items-center gap-5 rounded-md border-2 border-ostrel-gold/70 bg-ostrel-gold/15 px-6 py-5 shadow-sm">
          <span className="inline-block h-3 w-3 shrink-0 rotate-45 bg-ostrel-gold" />
          <div className="min-w-0">
            <p className="text-base text-ostrel-ink">
              <span className="tnum text-xl font-bold">
                {totalMapping > 0
                  ? `${totalMapping.toLocaleString('sv-SE')} ${totalMapping === 1 ? 'object' : 'objects'} to map`
                  : 'Nothing to map'}
              </span>
              <span className="text-ostrel-ink-muted">
                {' '}
                across {pending.length} {pending.length === 1 ? 'system' : 'systems'}
                {totalSightings > 0 &&
                  ` · ${totalSightings} unknown ${totalSightings === 1 ? 'tag' : 'tags'} to review`}
              </span>
            </p>
            <ul className="mt-3 flex flex-wrap gap-2">
              {pending.map((entry) => (
                <li key={entry.system.unique_id}>
                  <Link
                    to={`/systems/${entry.system.unique_id}/mapping`}
                    className="inline-flex items-center gap-2 rounded-sm bg-ostrel-navy px-6 py-3 text-sm font-semibold uppercase tracking-wider text-ostrel-light transition-colors hover:bg-ostrel-raised focus:outline-none focus:ring-2 focus:ring-ostrel-gold"
                  >
                    <span className="normal-case tracking-normal">{entry.system.name}</span>
                    <span className="tnum">
                      {entry.split.mapping > 0
                        ? `${entry.split.mapping} to map`
                        : `${entry.split.sightings} tags`}{' '}
                      →
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  )
}
